import { useState, useRef, useEffect } from 'react'
import { X, Search, Plus } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectOption } from '@/components/ui/select'
import { useRecipeStore } from '@/stores/recipe-store'
import { MEAL_TYPES } from '@/lib/meal-types'

type DynamicTag = {
  id: string
  type: 'ingredient' | 'tag' | 'equipment'
  value: string
  label: string
}

const DIFFICULTY_OPTIONS = [
  { value: '', label: 'Alle moeilijkheidsgraden' },
  { value: 'gemakkelijk', label: 'Gemakkelijk' },
  { value: 'middel', label: 'Middel' },
  { value: 'moeilijk', label: 'Moeilijk' }
]

const TIME_OPTIONS = [
  { value: '', label: 'Alle tijden' },
  { value: '15', label: '≤ 15 minuten' },
  { value: '30', label: '≤ 30 minuten' },
  { value: '60', label: '≤ 1 uur' },
  { value: '120', label: '≤ 2 uur' }
]

export function SearchBuilder() {
  const { filters, setFilters, recipes } = useRecipeStore()
  const [searchInput, setSearchInput] = useState('')
  const [dynamicTags, setDynamicTags] = useState<DynamicTag[]>([])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Get all available ingredients, tags, and equipment for suggestions
  // Extract individual words from ingredients to support partial matching
  const allIngredients = Array.from(new Set(recipes.flatMap(recipe => 
    recipe.ingredients.flatMap(ing => {
      const cleaned = ing.toLowerCase().trim()
      // Extract meaningful words (remove quantities, units, etc.)
      const words = cleaned.split(/[\s,\-()]+/).filter(word => 
        word.length > 2 && 
        !['gram', 'kg', 'liter', 'ml', 'cup', 'tbsp', 'tsp', 'stuks', 'snufje'].includes(word)
      )
      return [cleaned, ...words]
    })
  ))).sort()
  
  const allTags = Array.from(new Set(recipes.flatMap(recipe => recipe.tags))).sort()
  const allEquipment = Array.from(new Set(recipes.flatMap(recipe => recipe.equipment))).sort()

  // Generate suggestions based on input with partial matching
  useEffect(() => {
    if (searchInput.length < 1) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    const input = searchInput.toLowerCase()
    
    // Find ingredients that start with or contain the input
    const ingredientSuggestions = allIngredients
      .filter(ing => {
        const isAlreadyAdded = dynamicTags.some(tag => tag.value.toLowerCase() === ing.toLowerCase())
        return !isAlreadyAdded && (
          ing.startsWith(input) || 
          ing.includes(input) ||
          // Also check if any word in the ingredient starts with input
          ing.split(/[\s,\-()]+/).some(word => word.startsWith(input))
        )
      })
      .sort((a, b) => {
        // Prioritize exact starts, then word starts, then contains
        const aStarts = a.startsWith(input) ? 0 : a.split(/[\s,\-()]+/).some(w => w.startsWith(input)) ? 1 : 2
        const bStarts = b.startsWith(input) ? 0 : b.split(/[\s,\-()]+/).some(w => w.startsWith(input)) ? 1 : 2
        return aStarts - bStarts || a.length - b.length
      })
      .slice(0, 5)
    
    const tagSuggestions = allTags
      .filter(tag => {
        const isAlreadyAdded = dynamicTags.some(dt => dt.value.toLowerCase() === tag.toLowerCase())
        return !isAlreadyAdded && tag.toLowerCase().includes(input)
      })
      .slice(0, 3)
    
    const equipmentSuggestions = allEquipment
      .filter(eq => {
        const isAlreadyAdded = dynamicTags.some(dt => dt.value.toLowerCase() === eq.toLowerCase())
        return !isAlreadyAdded && eq.toLowerCase().includes(input)
      })
      .slice(0, 3)

    // Add the current input as a suggestion if it's not empty and not already in suggestions
    const suggestions = []
    
    // Always show current input as first option
    if (input.trim() && !ingredientSuggestions.some(ing => ing.toLowerCase() === input)) {
      suggestions.push(input.trim())
    }
    
    suggestions.push(...ingredientSuggestions, ...tagSuggestions, ...equipmentSuggestions)
    
    const uniqueSuggestions = Array.from(new Set(suggestions)).slice(0, 8)

    setSuggestions(uniqueSuggestions)
    setShowSuggestions(uniqueSuggestions.length > 0)
  }, [searchInput, dynamicTags, allIngredients, allTags, allEquipment])

  // Update filters when dynamic tags change
  useEffect(() => {
    const ingredients = dynamicTags.filter(tag => tag.type === 'ingredient').map(tag => tag.value)
    const tags = dynamicTags.filter(tag => tag.type === 'tag').map(tag => tag.value)
    const equipment = dynamicTags.filter(tag => tag.type === 'equipment').map(tag => tag.value)

    // Create search string from ingredients for the main search filter
    const searchTerms = ingredients.join(' ')
    
    setFilters({
      ...filters,
      search: searchTerms,
      tags: tags.length > 0 ? tags : undefined,
      equipment: equipment.length > 0 ? equipment : undefined
    })
  }, [dynamicTags])

  const addDynamicTag = (value: string, type: DynamicTag['type'] = 'ingredient') => {
    const newTag: DynamicTag = {
      id: `${type}-${value}-${Date.now()}`,
      type,
      value,
      label: value
    }

    setDynamicTags(prev => [...prev, newTag])
    setSearchInput('')
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  const removeDynamicTag = (tagId: string) => {
    setDynamicTags(prev => prev.filter(tag => tag.id !== tagId))
  }

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchInput.trim()) {
      e.preventDefault()
      addDynamicTag(searchInput.trim(), 'ingredient')
    }
  }

  const selectSuggestion = (suggestion: string) => {
    // Determine type based on which array it came from
    let type: DynamicTag['type'] = 'ingredient'
    if (allTags.some(tag => tag.toLowerCase() === suggestion.toLowerCase())) type = 'tag'
    else if (allEquipment.some(eq => eq.toLowerCase() === suggestion.toLowerCase())) type = 'equipment'
    
    addDynamicTag(suggestion, type)
  }

  const handleDifficultyChange = (value: string) => {
    setFilters({ ...filters, difficulty: value || undefined })
  }

  const handleMealTypeChange = (value: string) => {
    setFilters({ ...filters, meal_type: value || undefined })
  }

  const handleTimeChange = (value: string) => {
    setFilters({ ...filters, maxTime: value ? parseInt(value) : undefined })
  }

  const clearAllFilters = () => {
    setDynamicTags([])
    setFilters({})
    setSearchInput('')
  }

  const getTagColor = (type: DynamicTag['type']) => {
    switch (type) {
      case 'ingredient':
        return 'bg-green-100 text-green-800 hover:bg-green-200'
      case 'tag':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200'
      case 'equipment':
        return 'bg-purple-100 text-purple-800 hover:bg-purple-200'
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200'
    }
  }

  const getTagIcon = (type: DynamicTag['type']) => {
    switch (type) {
      case 'ingredient':
        return '🥕'
      case 'tag':
        return '🏷️'
      case 'equipment':
        return '🔧'
      default:
        return '🔍'
    }
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5" />
          Zoek Recepten
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* High-level Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Difficulty Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Moeilijkheidsgraad</label>
            <Select 
              value={filters.difficulty || ''} 
              onChange={(e) => handleDifficultyChange(e.target.value)}
            >
              {DIFFICULTY_OPTIONS.map(option => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          </div>

          {/* Meal Type Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Maaltijdtype</label>
            <Select 
              value={filters.meal_type || ''} 
              onChange={(e) => handleMealTypeChange(e.target.value)}
            >
              <SelectOption value="">Alle maaltijden</SelectOption>
              {MEAL_TYPES.map(mealType => (
                <SelectOption key={mealType.key} value={mealType.key}>
                  {mealType.icon} {mealType.name}
                </SelectOption>
              ))}
            </Select>
          </div>

          {/* Time Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Bereidingstijd</label>
            <Select 
              value={filters.maxTime?.toString() || ''} 
              onChange={(e) => handleTimeChange(e.target.value)}
            >
              {TIME_OPTIONS.map(option => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>

        {/* Dynamic Search Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Ingrediënten, tags en apparatuur
          </label>
          <div className="relative">
            <Input
              ref={inputRef}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setShowSuggestions(suggestions.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Type 'pota' voor aardappel, 'kip' voor kip, etc. en druk Enter..."
              className="pr-10"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => addDynamicTag(searchInput.trim(), 'ingredient')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-10 bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                {suggestions.map((suggestion, index) => {
                  let type: DynamicTag['type'] = 'ingredient'
                  let isExact = false
                  
                  // Check if it's an exact match from our data
                  if (allTags.some(tag => tag.toLowerCase() === suggestion.toLowerCase())) {
                    type = 'tag'
                    isExact = true
                  } else if (allEquipment.some(eq => eq.toLowerCase() === suggestion.toLowerCase())) {
                    type = 'equipment'
                    isExact = true
                  } else if (allIngredients.some(ing => ing.toLowerCase() === suggestion.toLowerCase())) {
                    isExact = true
                  }
                  
                  return (
                    <button
                      key={index}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm"
                      onClick={() => selectSuggestion(suggestion)}
                    >
                      <span className="text-lg">{getTagIcon(type)}</span>
                      <span className="flex-1">
                        {/* Highlight the matching part */}
                        {searchInput.length > 0 && suggestion.toLowerCase().includes(searchInput.toLowerCase()) ? (
                          <>
                            {suggestion.split(new RegExp(`(${searchInput})`, 'gi')).map((part, i) => 
                              part.toLowerCase() === searchInput.toLowerCase() ? 
                                <mark key={i} className="bg-yellow-200">{part}</mark> : part
                            )}
                          </>
                        ) : (
                          suggestion
                        )}
                      </span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {isExact ? (
                          type === 'ingredient' ? 'ingrediënt' : type
                        ) : (
                          'zoekterm'
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Tip: Gebruik gedeeltelijke woorden (bijv. "pota" voor aardappel). Druk Enter of klik op suggesties.
          </p>
        </div>

        {/* Dynamic Tags Display */}
        {dynamicTags.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Actieve filters</label>
            <div className="flex flex-wrap gap-2">
              {dynamicTags.map(tag => (
                <Badge
                  key={tag.id}
                  className={`flex items-center gap-1 px-3 py-1 ${getTagColor(tag.type)}`}
                >
                  <span className="text-sm">{getTagIcon(tag.type)}</span>
                  <span>{tag.label}</span>
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-red-600"
                    onClick={() => removeDynamicTag(tag.id)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Clear All Button */}
        {(dynamicTags.length > 0 || filters.difficulty || filters.meal_type || filters.maxTime) && (
          <Button
            variant="outline"
            onClick={clearAllFilters}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          >
            <X className="h-4 w-4 mr-2" />
            Alle filters wissen
          </Button>
        )}
      </CardContent>
    </Card>
  )
}